import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-kid-card-beta',
  templateUrl: './kid-card-beta.component.html',
  styleUrls: ['./kid-card-beta.component.scss']
})
export class KidCardBetaComponent implements OnInit, AfterViewInit {
  @Input() response: any;
  @Input() i: number;
  @Input() j: number;
  @Input() isDelivering: boolean = false;
  @Output() noDeliver = new EventEmitter<boolean>();
  @Output() noDeliverReason = new EventEmitter<string>();
  @Output() clicked = new EventEmitter<number>();

  @ViewChild('studentName') studentNameElement!: ElementRef;

  stat: string = '';
  baseuUlr = environment.img_url;
  student_status: string;
  userId: string;
  loggedInUserId: string;
  isTextOverflowing = false;

  constructor(private translate: TranslateService) { }

  ngOnInit() {
    this.student_status = localStorage.getItem("studentStatus") || '';
    this.loggedInUserId = localStorage.getItem("userId") || '';
    this.initializeStudentStatus();
  }

  ngAfterViewInit() {
    this.checkTextOverflow();
  }

  private initializeStudentStatus() {
    if (this.response[this.i]?.students[this.j]?.request_status) {
      this.stat = this.response[this.i].students[this.j].request_status.name;
      this.setUserId();
    } else {
      if (this.response[this.i]?.students[this.j]?.requests?.length > 0) {
        const len = this.response[this.i].students[this.j].requests.length;
        this.stat = this.response[this.i].students[this.j].requests[len - 1].pivot.status;
      } else {
        this.stat = '';
      }
    }
  }

  private setUserId() {
    switch (this.student_status) {
      case "with_teacher":
        this.userId = this.response[this.i].students[this.j].request_status.pivot['teacher_id'];
        break;
      case "with_supervisor":
        this.userId = this.response[this.i].students[this.j].request_status.pivot['supervisor_id'];
        break;
    }
  }

  private checkTextOverflow() {
    setTimeout(() => {
      const studentNameEl = this.studentNameElement?.nativeElement;
      if (studentNameEl) {
        const textContentEl = studentNameEl.querySelector('.name-text');
        if (textContentEl && textContentEl.scrollWidth > studentNameEl.clientWidth) {
          this.isTextOverflowing = true;
        }
      }
    }, 100);
  }

  public isStudentLate(): boolean {
    if (this.response[this.i]?.students[this.j]?.request) {
      return this.response[this.i].students[this.j].request[0].is_late === 1;
    } else if (this.response[this.i]?.students[this.j]?.requests) {
      return this.response[this.i].students[this.j].requests[0].is_late === 1;
    }
    return false;
  }

  studentClicked() {
    const observerMode = localStorage.getItem('observer_mode') ? 
      JSON.parse(localStorage.getItem('observer_mode') || 'false') : false;
    
    if (observerMode) {
      this.noDeliver.emit(true);
      this.noDeliverReason.emit('observer_mode');
      return;
    }

    if (this.isActionable()) {
      const requestId = this.getRequestId();
      if (requestId) {
        this.clicked.emit(requestId);
      }
    } else {
      this.handleNoDeliver();
    }
  }

  isActionable(): boolean {
    return this.stat !== 'pending' && 
           (this.stat === this.student_status || this.student_status === 'ADMIN');
  }

  private getRequestId(): number | null {
    if (this.response[this.i]?.students[this.j]?.requests) {
      return this.response[this.i].students[this.j].requests[0].id;
    } else if (this.response[this.i]?.students[this.j]?.request) {
      return this.response[this.i].students[this.j].request[0].id;
    }
    return null;
  }

  private handleNoDeliver() {
    if (this.student_status === 'ADMIN') {
      this.noDeliverReason.emit('Admin');
    } else if (this.stat === 'with_parent') {
      this.noDeliverReason.emit('parent');
    } else if (this.stat === 'pending') {
      this.noDeliverReason.emit('pending');
    } else {
      this.noDeliverReason.emit('nopermission');
    }
    this.noDeliver.emit(true);
  }

  getStatusText(): string {
    // Map the status to match the design in the image
    // If student is late, show "Hurry Up" regardless of other status
    if (this.isStudentLate()) {
      return this.translate.instant('calling-screen.status.hurry-up');
    }
    switch (this.stat) {
      case 'pending': return this.translate.instant('calling-screen.status.get-ready');
      case 'with_teacher': return this.translate.instant('calling-screen.status.lets-go');
      case 'with_supervisor': return this.translate.instant('calling-screen.status.lets-go');
    }
  }

  getCardStatusClass(): string {
    // Check status directly instead of comparing translated strings
    if (this.isStudentLate()) {
      return 'hurry-up-card';
    } else if (this.stat === 'with_teacher' || this.stat === 'with_supervisor') {
      return 'lets-go-card';
    } else if (this.stat === 'pending') {
      return 'get-ready-card';
    }
  }

  getStatusBadgeClass(): string {
    // Check status directly instead of comparing translated strings
    if (this.isStudentLate()) {
      return 'hurry-up-badge';
    } else if (this.stat === 'with_teacher' || this.stat === 'with_supervisor') {
      return 'lets-go-badge';
    } else if (this.stat === 'pending') {
      return 'get-ready-badge';
    }
  }


  getGateName(): string {
    if (this.response[this.i]?.students[this.j]?.requests) {
      return this.response[this.i].students[this.j].requests[0].gate?.name || '';
    } else if (this.response[this.i]?.students[this.j]?.request) {
      return this.response[this.i].students[this.j].request[0].gate?.name || '';
    }
    return '';
  }

  getGateTooltip(): string {
    return this.getGateName();
  }

  getTruncatedGateName(): string {
    const fullGateName = this.getGateName();
    const words = fullGateName.trim().split(/\s+/);
    
    // First, limit by word count (max 5 words)
    let result = words.length <= 5 ? fullGateName : words.slice(0, 5).join(' ');
    
    // Then, limit by character length to prevent layout breaking
    // Using same limits as student name for consistency
    const maxCharacters = 25;
    if (result.length > maxCharacters) {
      // Truncate at word boundaries to avoid cutting words in the middle
      const wordsInResult = result.split(/\s+/);
      let truncated = '';
      
      for (const word of wordsInResult) {
        const testString = truncated ? `${truncated} ${word}` : word;
        if (testString.length <= maxCharacters) {
          truncated = testString;
        } else {
          break;
        }
      }
      
      result = truncated || result.substring(0, maxCharacters).trim();
    }
    
    return result;
  }

  getTruncatedName(): string {
    const fullName = this.response[this.i]?.students[this.j]?.name || '';
    const words = fullName.trim().split(/\s+/);
    
    // First, limit by word count (max 5 words)
    let result = words.length <= 5 ? fullName : words.slice(0, 5).join(' ');
    
    // Then, limit by character length to prevent layout breaking
    // Card width: 217px - 32px padding = 185px available
    // Inter font 16px weight 600: ~8-10px per character = ~18-20 characters max safe
    const maxCharacters = 25;
    if (result.length > maxCharacters) {
      // Truncate at word boundaries to avoid cutting words in the middle
      const wordsInResult = result.split(/\s+/);
      let truncated = '';
      
      for (const word of wordsInResult) {
        const testString = truncated ? `${truncated} ${word}` : word;
        if (testString.length <= maxCharacters) {
          truncated = testString;
        } else {
          break;
        }
      }
      
      result = truncated || result.substring(0, maxCharacters).trim();
    }
    
    return result;
  }

  hasCustomImage(): boolean {
    const imagePath = this.response[this.i]?.students[this.j]?.image_path;
    if (!imagePath) return false;
    
    // Extract just the filename from the path
    const filename = imagePath.split('/').pop();
    return filename !== 'avatar.png';
  }
}
